import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const TIMELINE_HEIGHT = 120;
const HISTOGRAM_HEIGHT = 200;
const MARGIN = { top: 20, right: 20, bottom: 40, left: 50 };

const FIELD_LABELS = {
  pest365: 'Pest 365',
  pest365Plus: 'Pest 365 Plus',
  initialServiceVisit: 'Initial Service Visit',
  oneTime: 'One-Time',
  mosquitoTickControl: 'Mosquito & Tick Control',
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '12px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '2px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '0',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  label: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
  },
  select: {
    fontSize: '13px',
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    backgroundColor: '#ffffff',
    color: '#111827',
    cursor: 'pointer',
    outline: 'none',
  },
  container: {
    overflowX: 'auto',
  },
  sectionLabel: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#374151',
    marginTop: '20px',
    marginBottom: '8px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  statsRow: {
    display: 'flex',
    gap: '16px',
    marginTop: '20px',
    flexWrap: 'wrap',
  },
  statCard: {
    flex: '1 1 0',
    minWidth: '120px',
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '12px 16px',
    textAlign: 'center',
  },
  statValue: {
    fontSize: '20px',
    fontWeight: 700,
    color: '#1f2937',
    marginBottom: '2px',
  },
  statLabel: {
    fontSize: '11px',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  verdictBadge: {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '10px',
    fontSize: '13px',
    fontWeight: 600,
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

export default function PriceRunsTestChart({ data }) {
  const timelineSvgRef = useRef(null);
  const histogramSvgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [selectedField, setSelectedField] = useState('');

  const fieldList = useMemo(() => {
    if (!data || !data.fields || data.fields.length === 0) return [];
    return data.fields;
  }, [data]);

  useEffect(() => {
    if (fieldList.length > 0 && !selectedField) {
      setSelectedField(fieldList[0].field);
    }
  }, [fieldList, selectedField]);

  const fieldData = useMemo(() => {
    if (!fieldList.length || !selectedField) return null;
    return fieldList.find((f) => f.field === selectedField) || null;
  }, [fieldList, selectedField]);

  // Draw timeline (horizontal run blocks)
  useEffect(() => {
    if (!timelineSvgRef.current || !fieldData || !fieldData.runs || fieldData.runs.length === 0) return;

    const svg = d3.select(timelineSvgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = TIMELINE_HEIGHT - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', TIMELINE_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    const runs = fieldData.runs;
    const totalLength = runs.reduce((sum, r) => sum + r.length, 0);

    // X scale: proportional to run length
    const xScale = d3.scaleLinear().domain([0, totalLength]).range([0, innerW]);

    // Find longest run for annotation
    let longestRunIndex = 0;
    let maxLen = 0;
    runs.forEach((r, i) => {
      if (r.length > maxLen) {
        maxLen = r.length;
        longestRunIndex = i;
      }
    });

    // Draw run blocks
    let cumLen = 0;
    runs.forEach((run, i) => {
      const x0 = xScale(cumLen);
      const blockWidth = xScale(cumLen + run.length) - x0;
      const isLongest = i === longestRunIndex;
      const color = run.type === 'up' ? '#16a34a' : '#dc2626';

      // Block rect
      g.append('rect')
        .attr('x', x0)
        .attr('y', 4)
        .attr('width', Math.max(1, blockWidth - 1))
        .attr('height', innerH - 8)
        .attr('fill', color)
        .attr('fill-opacity', isLongest ? 0.85 : 0.6)
        .attr('rx', 2)
        .attr('stroke', isLongest ? '#1f2937' : 'none')
        .attr('stroke-width', isLongest ? 2 : 0)
        .attr('stroke-dasharray', isLongest ? '4,2' : 'none')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            type: run.type,
            length: run.length,
            startDate: run.startDate,
            endDate: run.endDate,
            isLongest,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });

      // Run length label (if block is wide enough)
      if (blockWidth > 20) {
        g.append('text')
          .attr('x', x0 + blockWidth / 2)
          .attr('y', innerH / 2 + 2)
          .attr('text-anchor', 'middle')
          .attr('dominant-baseline', 'central')
          .attr('font-size', blockWidth > 30 ? '11px' : '9px')
          .attr('font-weight', 600)
          .attr('fill', '#fff')
          .attr('pointer-events', 'none')
          .text(run.length);
      }

      // Longest run annotation
      if (isLongest) {
        const midX = x0 + blockWidth / 2;
        g.append('line')
          .attr('x1', midX)
          .attr('x2', midX)
          .attr('y1', -2)
          .attr('y2', 4)
          .attr('stroke', '#1f2937')
          .attr('stroke-width', 1);

        g.append('text')
          .attr('x', midX)
          .attr('y', -6)
          .attr('text-anchor', 'middle')
          .attr('font-size', '9px')
          .attr('font-weight', 600)
          .attr('fill', '#1f2937')
          .text('Longest run');
      }

      cumLen += run.length;
    });

    // Date labels at start and end
    if (runs.length > 0) {
      g.append('text')
        .attr('x', 0)
        .attr('y', innerH + 12)
        .attr('font-size', '10px')
        .attr('fill', '#6b7280')
        .attr('text-anchor', 'start')
        .text(formatDateLabel(runs[0].startDate));

      g.append('text')
        .attr('x', innerW)
        .attr('y', innerH + 12)
        .attr('font-size', '10px')
        .attr('fill', '#6b7280')
        .attr('text-anchor', 'end')
        .text(formatDateLabel(runs[runs.length - 1].endDate));
    }

    return () => {
      svg.selectAll('*').remove();
    };
  }, [fieldData]);

  // Draw histogram (run-length distribution vs expected)
  useEffect(() => {
    if (!histogramSvgRef.current || !fieldData || !fieldData.runs || fieldData.runs.length === 0) return;

    const svg = d3.select(histogramSvgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const margin = { top: 20, right: 20, bottom: 40, left: 50 };
    const innerW = width - margin.left - margin.right;
    const innerH = HISTOGRAM_HEIGHT - margin.top - margin.bottom;

    svg.attr('width', width).attr('height', HISTOGRAM_HEIGHT);

    const g = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

    const runs = fieldData.runs;
    const runLengths = runs.map((r) => r.length);
    const maxRunLen = d3.max(runLengths) || 1;

    // Count frequency of each run length
    const freq = {};
    runLengths.forEach((len) => {
      freq[len] = (freq[len] || 0) + 1;
    });

    const buckets = [];
    for (let len = 1; len <= maxRunLen; len++) {
      buckets.push({ length: len, observed: freq[len] || 0 });
    }

    // Expected distribution under randomness: geometric distribution
    // P(run length = k) = (0.5)^k for a two-state random walk
    // Expected count = totalRuns * (0.5)^k
    const totalRuns = fieldData.totalRuns || runs.length;
    buckets.forEach((b) => {
      b.expected = totalRuns * Math.pow(0.5, b.length);
    });

    // X scale
    const x = d3.scaleBand()
      .domain(buckets.map((b) => b.length))
      .range([0, innerW])
      .padding(0.3);

    // Y scale
    const yMax = d3.max(buckets, (b) => Math.max(b.observed, b.expected)) || 1;
    const y = d3.scaleLinear()
      .domain([0, yMax * 1.15])
      .nice()
      .range([innerH, 0]);

    // Grid lines
    const yTicks = y.ticks(5);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    const barWidth = x.bandwidth() / 2;

    // Observed bars
    g.selectAll('.bar-observed')
      .data(buckets)
      .enter()
      .append('rect')
      .attr('x', (d) => x(d.length))
      .attr('y', (d) => y(d.observed))
      .attr('width', barWidth)
      .attr('height', (d) => innerH - y(d.observed))
      .attr('fill', '#2563eb')
      .attr('fill-opacity', 0.7)
      .attr('rx', 1);

    // Expected bars
    g.selectAll('.bar-expected')
      .data(buckets)
      .enter()
      .append('rect')
      .attr('x', (d) => x(d.length) + barWidth)
      .attr('y', (d) => y(d.expected))
      .attr('width', barWidth)
      .attr('height', (d) => innerH - y(d.expected))
      .attr('fill', '#f59e0b')
      .attr('fill-opacity', 0.5)
      .attr('rx', 1)
      .attr('stroke', '#f59e0b')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3,2');

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).tickSize(0).tickFormat((d) => d))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // X axis label
    g.append('text')
      .attr('x', innerW / 2)
      .attr('y', innerH + 30)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Run Length');

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickSize(0))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Y axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -innerH / 2)
      .attr('y', -36)
      .attr('text-anchor', 'middle')
      .attr('font-size', '11px')
      .attr('fill', '#6b7280')
      .text('Count');

    return () => {
      svg.selectAll('*').remove();
    };
  }, [fieldData]);

  // Empty state
  if (!data || !data.fields || data.fields.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Price Runs Analysis</div>
          <div style={styles.subtitle}>Testing for randomness in price movements</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No runs test data available
        </div>
      </div>
    );
  }

  const verdictColor = fieldData && fieldData.isRandom ? '#16a34a' : '#dc2626';
  const verdictText = fieldData && fieldData.isRandom ? 'Random' : 'Non-Random';
  const verdictBg = fieldData && fieldData.isRandom ? '#f0fdf4' : '#fef2f2';

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Price Runs Analysis</div>
        <div style={styles.subtitle}>Testing for randomness in price movements</div>
      </div>

      {/* Field selector */}
      <div style={styles.controls}>
        <span style={styles.label}>Field:</span>
        <select
          style={styles.select}
          value={selectedField}
          onChange={(e) => {
            setSelectedField(e.target.value);
            setTooltip(null);
          }}
        >
          {fieldList.map((f) => (
            <option key={f.field} value={f.field}>
              {FIELD_LABELS[f.field] || f.field}
            </option>
          ))}
        </select>
        {fieldData && (
          <span
            style={{
              ...styles.verdictBadge,
              color: verdictColor,
              background: verdictBg,
            }}
          >
            {verdictText} (p={fieldData.pValue != null ? fieldData.pValue.toFixed(3) : 'N/A'})
          </span>
        )}
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <div style={{ width: 14, height: 10, background: '#16a34a', borderRadius: 2, opacity: 0.7 }} />
          <span>Up run (consecutive increases)</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 14, height: 10, background: '#dc2626', borderRadius: 2, opacity: 0.7 }} />
          <span>Down run (consecutive decreases)</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 14, height: 10, background: '#2563eb', borderRadius: 2, opacity: 0.7 }} />
          <span>Observed</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{ width: 14, height: 10, background: '#f59e0b', borderRadius: 2, opacity: 0.5, border: '1px dashed #f59e0b' }} />
          <span>Expected (random)</span>
        </div>
      </div>

      {/* Run timeline */}
      <div style={styles.sectionLabel}>Run Sequence Timeline</div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={timelineSvgRef} />
      </div>

      {/* Histogram */}
      <div style={styles.sectionLabel}>Run Length Distribution</div>
      <div style={styles.container}>
        <svg ref={histogramSvgRef} />
      </div>

      {/* Summary stats */}
      {fieldData && (
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{fieldData.totalRuns}</div>
            <div style={styles.statLabel}>Total Runs</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>
              {fieldData.expectedRuns != null ? fieldData.expectedRuns.toFixed(1) : 'N/A'}
            </div>
            <div style={styles.statLabel}>Expected Runs</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>
              {fieldData.zScore != null
                ? (fieldData.zScore >= 0 ? '+' : '') + fieldData.zScore.toFixed(2)
                : 'N/A'}
            </div>
            <div style={styles.statLabel}>Z-Score</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>
              {fieldData.pValue != null ? fieldData.pValue.toFixed(4) : 'N/A'}
            </div>
            <div style={styles.statLabel}>P-Value</div>
          </div>
          <div style={styles.statCard}>
            <div style={{
              ...styles.statValue,
              color: fieldData.isRandom ? '#16a34a' : '#dc2626',
            }}>
              {fieldData.isRandom ? 'Random' : 'Non-Random'}
            </div>
            <div style={styles.statLabel}>Verdict (p {'>'} 0.05)</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>
              {fieldData.avgRunLength != null ? fieldData.avgRunLength.toFixed(1) : 'N/A'}
            </div>
            <div style={styles.statLabel}>Avg Run Length</div>
          </div>
        </div>
      )}

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.type === 'up' ? 'Up' : 'Down'} Run
            {tooltip.isLongest ? ' (Longest)' : ''}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Direction</span>
            <span style={{
              fontWeight: 600,
              color: tooltip.type === 'up' ? '#16a34a' : '#dc2626',
            }}>
              {tooltip.type === 'up' ? 'Increasing' : 'Decreasing'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Length</span>
            <span style={{ fontWeight: 600 }}>{tooltip.length} period{tooltip.length !== 1 ? 's' : ''}</span>
          </div>
          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: '4px', paddingTop: '4px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Start</span>
              <span style={{ fontWeight: 600 }}>{formatDateFull(tooltip.startDate)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>End</span>
              <span style={{ fontWeight: 600 }}>{formatDateFull(tooltip.endDate)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
